import { Fragment } from "react";
import type { BillPart, DeviceStatus, GeneralSettings, Order } from "../../api/types";
import { ReceiptHead, ReceiptQrBlock, startsCategory, VAT_LETTERS, VatTable } from "../../features/orders/Receipt";
import { describeModifier, noteText } from "../../features/sale/modifiers";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatDate, formatTime } from "../../lib/time";

interface Props {
  order: Order;
  shop: DeviceStatus["shop"];
  general: GeneralSettings | null;
  /** The waiter who asked for it ("Ober"). */
  waiter: string;
  /** When it was asked for. */
  at: string;
  paper?: 58 | 80;
  /** The verdeling at the bottom: per person or per guest, with his own QR when he asked for one. */
  parts?: readonly BillPart[];
}

/** "30-09-2026" and "19.42", as on the receipt. */
const billDate = (iso: string) => formatDate(iso).replace(/\//g, "-");
const billTime = (iso: string) => formatTime(iso).replace(":", ".");

/**
 * "🧾 Rekening" (prototype obBill): the provisional bill of a table, with the head of the receipt, when
 * the guests sat down, the waiter, every line with its VAT letter and the VAT table. It is not a receipt:
 * "Dit is geen kasticket. Afrekenen bij de ober."
 */
export function Bill({ order, shop, general, waiter, at, paper = 80, parts = [] }: Props) {
  const text = t.tafoApp.bill;
  const table = order.table ?? "";
  return (
    <article className={`bon bill${paper === 58 ? " w58" : ""}`} aria-label={text.title(table)}>
      <ReceiptHead shop={shop} general={general} />
      <div className="c blk billhead">{text.heading(table)}</div>
      <section className="blk">
        <div className="kv">
          <span>{text.kind}</span>
          <span>{text.kindValue(table)}</span>
        </div>
        <div className="kv">
          <span>{text.date}</span>
          <span>{billDate(at)}</span>
        </div>
        <div className="kv">
          <span>{text.time}</span>
          <span>{billTime(at)}</span>
        </div>
        <div className="kv">
          <span>{text.since}</span>
          <span>{formatTime(order.createdAt)}</span>
        </div>
        <div className="kv">
          <span>{text.waiter}</span>
          <span>{waiter}</span>
        </div>
      </section>
      <h4>{text.details}</h4>
      {order.lines.map((line, index) => (
        <Fragment key={index}>
          {startsCategory(order.lines, index) && <div className="cat">{line.category}</div>}
          <div className="it">
            <span>{line.quantity}x</span>
            <span>
              {line.receiptName ?? line.name}
              {line.variant !== null && ` ${line.variant}`}
            </span>
            <span className="pr">
              {line.free ? t.receipt.free : formatEuro(line.lineCents)} {VAT_LETTERS[line.vatRate]}
            </span>
            {line.modifiers.map((modifier, modifierIndex) => (
              <span className="mod" key={modifierIndex}>
                {describeModifier(modifier, false)}
              </span>
            ))}
            {line.note !== null && <span className="mod">{noteText(line.note)}</span>}
          </div>
        </Fragment>
      ))}
      {order.discountCents > 0 && (
        <div className="kv">
          <span>{t.sale.discount}</span>
          <span>−{formatEuro(order.discountCents)}</span>
        </div>
      )}
      <div className="rule" />
      <VatTable vat={order.vat} narrow={paper === 58} />
      <div className="total">
        <span>{t.sale.total}</span>
        <span>{formatEuro(order.totalCents)}</span>
      </div>
      {order.paidCents > 0 && (
        <div className="kv">
          <span>{t.tafoApp.pay.toPay}</span>
          <span>{formatEuro(order.totalCents - order.paidCents)}</span>
        </div>
      )}
      {order.receiptQr?.amountCents === order.totalCents - order.paidCents && <ReceiptQrBlock qr={order.receiptQr} />}
      {parts.length > 0 && (
        <>
          <div className="rule" />
          <h4>{text.split}</h4>
          {parts.map((part) => (
            <section
              className="blk billpart"
              key={`${part.kind}-${String(part.number)}`}
              aria-label={part.of === null ? text.guest(part.number) : text.person(part.number, part.of)}
            >
              <div className="kv billpart-head">
                <b>{part.of === null ? text.guest(part.number) : text.person(part.number, part.of)}</b>
                <b>{formatEuro(part.amountCents)}</b>
              </div>
              {part.lines.map((line, index) => (
                <div className="it" key={index}>
                  <span>{line.quantity}x</span>
                  <span>
                    {line.receiptName ?? line.name}
                    {line.variant !== null && ` ${line.variant}`}
                  </span>
                  <span className="pr">{line.free ? t.receipt.free : formatEuro(line.lineCents)}</span>
                  {line.modifiers.map((modifier, modifierIndex) => (
                    <span className="mod" key={modifierIndex}>
                      {describeModifier(modifier, false)}
                    </span>
                  ))}
                  {line.note !== null && <span className="mod">{noteText(line.note)}</span>}
                </div>
              ))}
              {part.qr !== null && <ReceiptQrBlock qr={part.qr} />}
            </section>
          ))}
        </>
      )}
      <div className="c billfoot">
        {text.notReceipt}
        <br />
        {text.payWaiter}
      </div>
      <div className="bontafo">
        <img src="/tafo-logo.png" alt="Tafo" />
        <span>{t.receipt.tafo}</span>
      </div>
    </article>
  );
}
